import { InvalidPurchaseError, type RecordPurchaseInput } from '../domain/cellar';
import { todayInParis, type CalendarDay } from '../domain/months';
import { t } from '../i18n';
import { NumberStepper } from './NumberStepper';
import { RatingStepper } from './RatingStepper';

/** Controlled form values for a purchase (FR-007, FR-008; schema version 6, owner 2026-10-06). */
export interface PurchaseFormValue {
  quantity: string;
  /** Euros as typed, e.g. "15.50"; the caller converts to cents. */
  unitPriceEuros: string;
  /** "2026-10-04", from the date picker. */
  purchasedOn: string;
  notes: string;
  /** With a quantity of 0 (a wine tasted elsewhere, owner 2026-10-11): its rating, 5 by default. */
  rating: number | undefined;
  /** …and its comment. */
  comment: string;
}

/** A day as the date picker writes it: "2026-10-04". */
export function dayText({ year, month, day }: CalendarDay): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * A price as typed, kept to whole euros (owner, 2026-10-15): the digits before any decimal
 * separator ("12,50" pasted gives "12"); anything else is dropped.
 */
export function wholeEuros(text: string): string {
  return (text.split(/[.,]/)[0] ?? '').replace(/\D/g, '');
}

/** Whole euros as typed (owner, 2026-10-15: no decimals) into cents; empty is 0. */
export function eurosToCents(text: string): number {
  return Math.round(Number(text.replace(',', '.') || '0')) * 100;
}

/** "2026-10-04" from the date picker as a day; undefined if it is not one. */
function parseDay(text: string): CalendarDay | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return undefined;
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

/**
 * The purchase part of `recordPurchase`'s input from the form: quantity, price, month and day,
 * details, and for a quantity of 0 the tasting elsewhere (owner, 2026-10-11). The caller adds the
 * wine. Throws when the date is not one.
 */
export function purchaseInputFrom(value: PurchaseFormValue): Omit<RecordPurchaseInput, 'wine'> {
  const day = parseDay(value.purchasedOn);
  if (!day) throw new InvalidPurchaseError(t('error.invalidDate'), 'date');
  return {
    quantity: Number(value.quantity),
    unitPriceCents: eurosToCents(value.unitPriceEuros),
    purchasedOn: { year: day.year, month: day.month },
    purchasedDay: day.day,
    notes: value.notes,
    ...(value.quantity === '0'
      ? {
          tastedElsewhere: {
            ...(value.rating !== undefined ? { rating: value.rating } : {}),
            ...(value.comment.trim() !== '' ? { notes: value.comment } : {}),
          },
        }
      : {}),
  };
}

export function defaultPurchaseFields(today: CalendarDay = todayInParis()): PurchaseFormValue {
  return {
    quantity: '1',
    unitPriceEuros: '',
    purchasedOn: dayText(today),
    notes: '',
    rating: 5,
    comment: '',
  };
}

interface Props {
  value: PurchaseFormValue;
  onChange: (value: PurchaseFormValue) => void;
}

/**
 * "Détails de l'achat" (FR-007, FR-008; owner 2026-10-06): quantity (1 by default, with − and +
 * since 2026-10-09), unit price, the day with a date picker (today by default, never later) and
 * "Détails". ("Vendeur" was removed on 2026-10-09.) A quantity of 0 is a wine tasted elsewhere
 * (owner, 2026-10-11): the price may stay empty, and a rating and comment appear for its history.
 */
export function PurchaseFields({ value, onChange }: Props) {
  function set<K extends keyof PurchaseFormValue>(key: K, fieldValue: PurchaseFormValue[K]) {
    onChange({ ...value, [key]: fieldValue });
  }

  return (
    <fieldset>
      <legend>{t('purchase.legend')}</legend>

      <NumberStepper
        min={0}
        id="quantity"
        label={t('purchase.quantity')}
        value={Number(value.quantity) || 0}
        decreaseLabel={t('drink.bottlesLess')}
        increaseLabel={t('drink.bottlesMore')}
        onChange={(quantity) => set('quantity', String(quantity))}
      />
      {value.quantity === '0' && (
        <div className="purchase-fields__tasted">
          <p>{t('purchase.tastedElsewhere')}</p>
          <RatingStepper
            label={t('drink.rating')}
            value={value.rating}
            onChange={(rating) => onChange({ ...value, rating })}
          />
          <label htmlFor="tastedComment">{t('drink.notes')}</label>
          <textarea
            id="tastedComment"
            rows={2}
            value={value.comment}
            onChange={(e) => set('comment', e.target.value)}
          />
        </div>
      )}

      <label htmlFor="unitPrice">{t('purchase.unitPrice')}</label>
      {/* Whole euros only (owner, 2026-10-15): anything but digits is dropped as it is typed. */}
      <input
        id="unitPrice"
        required={value.quantity !== '0'}
        inputMode="numeric"
        value={value.unitPriceEuros}
        onChange={(e) => set('unitPriceEuros', wholeEuros(e.target.value))}
      />

      <label htmlFor="purchasedOn">{t('purchase.date')}</label>
      <input
        id="purchasedOn"
        type="date"
        required
        max={dayText(todayInParis())}
        value={value.purchasedOn}
        onChange={(e) => set('purchasedOn', e.target.value)}
      />

      <label htmlFor="purchaseNotes">{t('purchase.notes')}</label>
      <textarea
        id="purchaseNotes"
        rows={3}
        value={value.notes}
        onChange={(e) => set('notes', e.target.value)}
      />
    </fieldset>
  );
}
